import React, { useState } from 'react';
import './AdminPayoutStatusModal.css';

interface AdminPayoutStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  statusData: any;
  loading?: boolean;
  /** PoolPay live enquiry vs Viyona fund-transfer status (labels + response shape hints). */
  source?: 'viyona' | 'poolpay';
}

const AdminPayoutStatusModal: React.FC<AdminPayoutStatusModalProps> = ({
  isOpen,
  onClose,
  statusData,
  loading = false,
  source = 'viyona',
}) => {
  // Hooks must be called at the top level, before any early returns
  const [showFullResponse, setShowFullResponse] = useState(false);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle different response structures
  const data = statusData?.data || statusData;

  const liveSectionTitle = source === 'poolpay' ? 'Live PoolPay Status' : 'Live ViyonaPay Status';
  const responseSectionTitle = source === 'poolpay' ? 'PoolPay Response' : 'ViyonaPay Response';
  const fullResponseTitle =
    source === 'poolpay' ? 'Complete PoolPay Response' : 'Complete Live ViyonaPay Response';
  const noResponseMessage =
    source === 'poolpay' ? 'No PoolPay response data' : 'No ViyonaPay data available';

  const getStatusColor = (status: string) => {
    const statusUpper = status?.toUpperCase() || '';
    switch (statusUpper) {
      case 'SUCCESS':
        return '#10B981';
      case 'FAILED':
        return '#EF4444';
      case 'PENDING':
      case 'PROCESSING':
        return '#F59E0B';
      default:
        return '#6B7280';
    }
  };

  const status = data?.status || 'UNKNOWN';
  const statusColor = getStatusColor(status);

  // Handle decrypted_response from different possible locations
  const decryptedResponse = data?.decrypted_response || 
                           statusData?.decrypted_response ||
                           (statusData?.data && statusData.data.decrypted_response);

  if (loading) {
    return (
      <div className="admin-payout-status-overlay" onClick={handleBackdropClick}>
        <div className="admin-payout-status-modal">
          <div className="admin-payout-status-header">
            <h2 className="admin-payout-status-title">Payout Status</h2>
            <button className="admin-payout-status-close" onClick={onClose}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
          <div className="admin-payout-status-content" style={{ padding: '40px', textAlign: 'center' }}>
            <div className="loading-spinner" style={{ margin: '0 auto 20px', width: '40px', height: '40px', border: '4px solid #f3f4f6', borderTop: '4px solid #667eea', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
            <p>{source === 'poolpay' ? 'Loading PoolPay status...' : 'Loading payout status...'}</p>
          </div>
        </div>
      </div>
    );
  }

  if (statusData?.error) {
    return (
      <div className="admin-payout-status-overlay" onClick={handleBackdropClick}>
        <div className="admin-payout-status-modal">
          <div className="admin-payout-status-header">
            <h2 className="admin-payout-status-title">Payout Status</h2>
            <button className="admin-payout-status-close" onClick={onClose}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
          <div className="admin-payout-status-content" style={{ padding: '40px', textAlign: 'center' }}>
            <div style={{ color: '#EF4444', fontSize: '18px', marginBottom: '10px' }}>❌</div>
            <p style={{ color: '#EF4444', fontSize: '16px' }}>{statusData.error}</p>
          </div>
          <div className="admin-payout-status-footer">
            <button className="admin-payout-status-btn" onClick={onClose}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Got it!
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="admin-payout-status-overlay" onClick={handleBackdropClick}>
      <div className="admin-payout-status-modal">
        {/* Header */}
        <div className="admin-payout-status-header">
          <h2 className="admin-payout-status-title">Payout Status</h2>
          <button className="admin-payout-status-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Content - Landscape Layout */}
        <div className="admin-payout-status-content">
          {/* Left Section - Live ViyonaPay Status */}
          <div className="admin-payout-status-section">
            <div className="admin-payout-status-section-header">
              <span className="status-dot" style={{ backgroundColor: statusColor }}></span>
              <h3>{liveSectionTitle}</h3>
            </div>
            <div className="admin-payout-status-section-body">
              <div className="status-badge-large" style={{ backgroundColor: statusColor }}>
                {status}
              </div>
              <div className="status-details-list">
                {data.merchant_reference_number && (
                  <div className="status-detail-item">
                    <span className="detail-label">Merchant Reference:</span>
                    <span className="detail-value">{data.merchant_reference_number}</span>
                  </div>
                )}
                {data.viyona_transaction_id && (
                  <div className="status-detail-item">
                    <span className="detail-label">Transaction ID:</span>
                    <span className="detail-value">{data.viyona_transaction_id}</span>
                  </div>
                )}
                {data.amount && (
                  <div className="status-detail-item">
                    <span className="detail-label">Amount:</span>
                    <span className="detail-value amount">₹{new Intl.NumberFormat('en-IN', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    }).format(parseFloat(data.amount))}</span>
                  </div>
                )}
                {data.bank_reference_number && (
                  <div className="status-detail-item">
                    <span className="detail-label">Bank Reference:</span>
                    <span className="detail-value">{data.bank_reference_number}</span>
                  </div>
                )}
                {data.error_message && (
                  <div className="status-detail-item">
                    <span className="detail-label">
                      {['SUCCESS', 'PENDING', 'PROCESSING', 'COMPLETED', 'FAILED'].includes(String(data.error_message).toUpperCase())
                        ? 'Message:'
                        : 'Error Message:'}
                    </span>
                    <span
                      className={['SUCCESS', 'PENDING', 'PROCESSING', 'COMPLETED', 'FAILED'].includes(String(data.error_message).toUpperCase()) ? 'detail-value' : 'detail-value error'}
                      style={['SUCCESS', 'PENDING', 'PROCESSING', 'COMPLETED', 'FAILED'].includes(String(data.error_message).toUpperCase())
                        ? { color: getStatusColor(String(data.error_message)), fontWeight: 600 }
                        : undefined}
                    >
                      {data.error_message}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Section - ViyonaPay Response */}
          <div className="admin-payout-status-section">
            <div className="admin-payout-status-section-header">
              <span className="section-icon">📄</span>
              <h3>{responseSectionTitle}</h3>
            </div>
            <div className="admin-payout-status-section-body">
              {decryptedResponse ? (
                <div className="response-preview">
                  <div className="response-preview-item">
                    <span className="preview-label">Status:</span>
                    <span className="preview-value">
                      {decryptedResponse?.response_body?.status ||
                        decryptedResponse?.status ||
                        decryptedResponse?.transaction_status ||
                        data.status ||
                        'N/A'}
                    </span>
                  </div>
                  <div className="response-preview-item">
                    <span className="preview-label">Transaction ID:</span>
                    <span className="preview-value">
                      {decryptedResponse?.response_body?.transaction_id ||
                        decryptedResponse?.transaction_id ||
                        data.viyona_transaction_id ||
                        'N/A'}
                    </span>
                  </div>
                  <div className="response-preview-item">
                    <span className="preview-label">Bank Reference:</span>
                    <span className="preview-value">
                      {decryptedResponse?.response_body?.bank_reference_number ||
                        decryptedResponse?.bank_reference_number ||
                        decryptedResponse?.acquirer_transaction_id ||
                        data.bank_reference_number ||
                        'N/A'}
                    </span>
                  </div>
                  {decryptedResponse?.response_body?.error_message ||
                  decryptedResponse?.error_message ||
                  decryptedResponse?.acquirer_status_message ? (
                    <div className="response-preview-item">
                      <span className="preview-label">Message:</span>
                      <span className="preview-value">
                        {decryptedResponse?.response_body?.error_message ||
                          decryptedResponse?.error_message ||
                          decryptedResponse?.acquirer_status_message}
                      </span>
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="no-data-message">{noResponseMessage}</div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Section - raw provider response */}
        {decryptedResponse && (
          <div className="admin-payout-status-full-response">
            <button 
              className="admin-payout-status-toggle-response"
              onClick={() => setShowFullResponse(!showFullResponse)}
            >
              <span className="section-icon">📋</span>
              <h3>{fullResponseTitle}:</h3>
              <span className="toggle-icon">{showFullResponse ? '▼' : '▶'}</span>
            </button>
            {showFullResponse && (
              <div className="admin-payout-status-json-container">
                <pre className="admin-payout-status-json">
                  {JSON.stringify(decryptedResponse, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="admin-payout-status-footer">
          <button className="admin-payout-status-btn" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Got it!
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminPayoutStatusModal;
